import React from 'react'
import { type QuothaPanelPayload } from '../api'
import TypewriterReveal from '../chat/components/TypewriterReveal'
import QuothaAvatar from './quotha/QuothaAvatar'

type QuothaPanelProps = {
  panel: QuothaPanelPayload
  variant?: 'attached' | 'inline'
  onDismiss?: () => void
  animateBody?: boolean
  onAnimateComplete?: () => void
}

const QuothaPanel = ({
  panel,
  variant = 'attached',
  onDismiss,
  animateBody = false,
  onAnimateComplete,
}: QuothaPanelProps) => {
  const primary =
    sanitizeText(panel.micro_commentary) ??
    sanitizeText(panel.evidence_basis) ??
    sanitizeText(panel.notes) ??
    sanitizeText(panel.assessment) ??
    sanitizeText(panel.body) ??
    sanitizeText(panel.summary)
  const auditRows = [
    ['Evidence basis', sanitizeText(panel.evidence_basis)],
    ['Strongest support', sanitizeText(panel.strongest_support)],
    ['Needs caution', sanitizeText(panel.needs_caution)],
    ['Speculative or reconstructed', sanitizeText(panel.speculative_or_reconstructed)],
    ['Would improve confidence', sanitizeText(panel.what_would_improve_confidence)],
  ].filter(([, value]) => value && value !== primary) as [string, string][]

  return (
    <div className={`quotha-panel ${variant}`} role="complementary" aria-label="Integrity Audit">
      {onDismiss && (
        <button
          type="button"
          className="quotha-dismiss-icon"
          onClick={onDismiss}
          aria-label="Dismiss Integrity Audit"
        >
          ×
        </button>
      )}
      <div className="quotha-panel-main">
        <div className="quotha-avatar-wrap">
          <QuothaAvatar size={52} alt="Quotha avatar" />
        </div>
        <div className="quotha-panel-copy">
          <div className="quotha-panel-label-row">
            <p className="quotha-panel-label">Integrity Audit</p>
            <span className="quotha-panel-label-subtext" title="Epistemic oversight">
              Epistemic oversight
            </span>
          </div>
          {primary ? (
            <p className="quotha-panel-body clamp-4">
              {animateBody ? (
                <TypewriterReveal
                  text={primary}
                  enabled={animateBody}
                  speedMsPerChar={12}
                  maxAnimatedChars={600}
                  onDone={onAnimateComplete}
                  className="typewriter-block"
                />
              ) : (
                primary
              )}
            </p>
          ) : (
            <p className="quotha-panel-body">Quotha shared additional context.</p>
          )}
          {auditRows.length > 0 && (
            <details className="quotha-audit-details">
              <summary>Evidence details</summary>
              <dl className="quotha-audit-list">
                {auditRows.map(([label, value]) => (
                  <div className="quotha-audit-row" key={label}>
                    <dt>{label}</dt>
                    <dd>{value}</dd>
                  </div>
                ))}
              </dl>
            </details>
          )}
        </div>
      </div>
    </div>
  )
}

const sanitizeText = (value?: string | null): string | null => {
  if (!value || typeof value !== 'string') return null
  const trimmed = value.trim()
  if (!trimmed) return null

  const lower = trimmed.toLowerCase()
  const needle = ' supported by '
  const idx = lower.indexOf(needle)
  if (idx === -1) return trimmed

  const prefix = trimmed.slice(0, idx).trimEnd().replace(/[.?!,:;]+$/u, '')
  return `${prefix} supported.`
}

export default QuothaPanel
